import { FormEvent, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import { useCollapsibleItemForm } from '../../hooks/useCollapsibleItemForm';
import {
  SalesProductChangeRequest,
  SalesProductChangeRequestUpsertPayload,
  SalesProductIssue,
  SalesProductIssueUpsertPayload,
  SalesProductLinkedApplication,
  SalesProductRequirement,
  SalesProductRequirementUpsertPayload,
  SalesProductRisk,
  SalesProductRiskUpsertPayload,
  SalesProductRoadmapItem,
  SalesProductRoadmapItemUpsertPayload,
  approveSalesProductChange,
  createSalesProductChange,
  createSalesProductIssue,
  createSalesProductRequirement,
  createSalesProductRisk,
  createSalesProductRoadmapItem,
  deleteSalesProductChange,
  deleteSalesProductChangeAzureLink,
  deleteSalesProductIssue,
  deleteSalesProductRequirement,
  deleteSalesProductRisk,
  deleteSalesProductRoadmapItem,
  linkSalesProductChangeAzure,
  listSalesProductChanges,
  listSalesProductIssues,
  listSalesProductLinkedApplications,
  listSalesProductRequirements,
  listSalesProductRisks,
  listSalesProductRoadmap,
  refreshSalesProductChangeAzureLink,
  rejectSalesProductChange,
  submitSalesProductChange,
  updateSalesProductChange,
  updateSalesProductIssue,
  updateSalesProductRequirement,
  updateSalesProductRisk,
  updateSalesProductRoadmapItem,
} from '../../services/salesProductWorkspaceApi';
import { formatDateBr } from '../../utils/formatDateBr';
import { getRequestErrorMessage } from '../../utils/getRequestErrorMessage';
import { PageCard } from '../layout/page/PageCard';
import { CollapsibleFormSection } from '../ui/CollapsibleFormSection';
import { DeleteIconButton } from '../ui/DeleteIconButton';
import { EditIconButton } from '../ui/EditIconButton';
import { FormActionBar } from '../ui/FormActionBar';
import { InlineAlert } from '../ui/InlineAlert';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { RequiredMark } from '../ui/RequiredMark';
import { ProductAzureLinksPanel } from './ProductAzureLinksPanel';
import { ProductBacklogPanel } from './ProductBacklogPanel';

export type ProductWorkspaceTabId = 'requirements' | 'backlog' | 'roadmap' | 'risks' | 'issues' | 'changes';

type ProductWorkspacePanelProps = {
  productId: string;
  canWrite: boolean;
  activeTab: ProductWorkspaceTabId;
};

const ROADMAP_KIND_OPTIONS = [
  { value: 'Phase', label: 'Fase' },
  { value: 'Milestone', label: 'Marco' },
  { value: 'Checkpoint', label: 'Checkpoint' },
  { value: 'Release', label: 'Release' },
];

const ROADMAP_STATUS_OPTIONS = [
  { value: 'Planned', label: 'Planejado' },
  { value: 'InProgress', label: 'Em andamento' },
  { value: 'Achieved', label: 'Atingido' },
  { value: 'Missed', label: 'Perdido' },
  { value: 'Cancelled', label: 'Cancelado' },
];

const RISK_PROBABILITY_OPTIONS = [
  { value: 'Low', label: 'Baixa' },
  { value: 'Medium', label: 'Média' },
  { value: 'High', label: 'Alta' },
  { value: 'VeryHigh', label: 'Muito alta' },
];

const RISK_IMPACT_OPTIONS = [
  { value: 'Low', label: 'Baixo' },
  { value: 'Medium', label: 'Médio' },
  { value: 'High', label: 'Alto' },
  { value: 'Critical', label: 'Crítico' },
];

const RISK_STATUS_OPTIONS = [
  { value: 'Identified', label: 'Identificado' },
  { value: 'Analyzing', label: 'Em análise' },
  { value: 'Mitigating', label: 'Mitigando' },
  { value: 'Monitoring', label: 'Monitorando' },
  { value: 'Closed', label: 'Encerrado' },
  { value: 'Materialized', label: 'Materializado' },
];

const ISSUE_SEVERITY_OPTIONS = [
  { value: 'Low', label: 'Baixa' },
  { value: 'Medium', label: 'Média' },
  { value: 'High', label: 'Alta' },
  { value: 'Critical', label: 'Crítica' },
];

const ISSUE_STATUS_OPTIONS = [
  { value: 'Open', label: 'Aberto' },
  { value: 'InAnalysis', label: 'Em análise' },
  { value: 'ActionTaken', label: 'Ação tomada' },
  { value: 'Resolved', label: 'Resolvido' },
  { value: 'Closed', label: 'Encerrado' },
];

const CHANGE_STATUS_LABELS: Record<string, string> = {
  Draft: 'Rascunho',
  Submitted: 'Submetido',
  UnderReview: 'Em análise',
  Approved: 'Aprovado',
  Rejected: 'Rejeitado',
  Implemented: 'Implementado',
  Cancelled: 'Cancelado',
};

const TAB_FORM_TITLES: Record<ProductWorkspaceTabId, { create: string; edit: string; summary: string }> = {
  requirements: { create: 'Novo requisito', edit: 'Editar requisito', summary: 'Clique para cadastrar um novo requisito' },
  backlog: { create: 'Novo item de backlog', edit: 'Editar item de backlog', summary: 'Clique para cadastrar um novo item' },
  roadmap: { create: 'Novo item de roadmap', edit: 'Editar item de roadmap', summary: 'Clique para cadastrar um novo item' },
  risks: { create: 'Novo risco', edit: 'Editar risco', summary: 'Clique para cadastrar um novo risco' },
  issues: { create: 'Novo problema', edit: 'Editar problema', summary: 'Clique para cadastrar um novo problema' },
  changes: { create: 'Nova mudança', edit: 'Editar mudança', summary: 'Clique para cadastrar uma nova mudança' },
};

function labelFor(options: { value: string; label: string }[], value: string): string {
  return options.find((option) => option.value === value)?.label ?? value;
}

function truncateText(value: string, maxLength: number): string {
  const text = value.trim();
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trimEnd()}…`;
}

function toDateInput(value?: string | null): string {
  return value ? value.slice(0, 10) : '';
}

const emptyRequirementForm = (): SalesProductRequirementUpsertPayload => ({
  title: '',
  validationCriteria: '',
});

const emptyRoadmapForm = (): SalesProductRoadmapItemUpsertPayload => ({
  title: '',
  kind: 'Milestone',
  status: 'Planned',
  sortOrder: 0,
});

const emptyRiskForm = (): SalesProductRiskUpsertPayload => ({
  title: '',
  probability: 'Medium',
  impact: 'Medium',
  treatmentPriority: 0,
  status: 'Identified',
  identifiedAtUtc: new Date().toISOString(),
});

const emptyIssueForm = (): SalesProductIssueUpsertPayload => ({
  title: '',
  severity: 'Medium',
  status: 'Open',
  identifiedAtUtc: new Date().toISOString(),
});

const emptyChangeForm = (): SalesProductChangeRequestUpsertPayload => ({
  title: '',
});

export function ProductWorkspacePanel({ productId, canWrite, activeTab }: ProductWorkspacePanelProps) {
  const { confirm } = useConfirmDialog();
  const { formExpanded, expandForm, collapseForm, handleCreateSuccess, toggleOrCancel } =
    useCollapsibleItemForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [applications, setApplications] = useState<SalesProductLinkedApplication[]>([]);
  const [requirements, setRequirements] = useState<SalesProductRequirement[]>([]);
  const [roadmapItems, setRoadmapItems] = useState<SalesProductRoadmapItem[]>([]);
  const [risks, setRisks] = useState<SalesProductRisk[]>([]);
  const [issues, setIssues] = useState<SalesProductIssue[]>([]);
  const [changes, setChanges] = useState<SalesProductChangeRequest[]>([]);
  const [requirementForm, setRequirementForm] = useState(emptyRequirementForm);
  const [roadmapForm, setRoadmapForm] = useState(emptyRoadmapForm);
  const [riskForm, setRiskForm] = useState(emptyRiskForm);
  const [issueForm, setIssueForm] = useState(emptyIssueForm);
  const [changeForm, setChangeForm] = useState(emptyChangeForm);

  const resetForm = useCallback(() => {
    setEditingId(null);
    setRequirementForm(emptyRequirementForm());
    setRoadmapForm(emptyRoadmapForm());
    setRiskForm(emptyRiskForm());
    setIssueForm(emptyIssueForm());
    setChangeForm(emptyChangeForm());
  }, []);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      if (activeTab === 'changes') {
        setApplications(await listSalesProductLinkedApplications(productId));
      }
      switch (activeTab) {
        case 'requirements':
          setRequirements(await listSalesProductRequirements(productId));
          break;
        case 'roadmap':
          setRoadmapItems(await listSalesProductRoadmap(productId));
          break;
        case 'risks':
          setRisks(await listSalesProductRisks(productId));
          break;
        case 'issues':
          setIssues(await listSalesProductIssues(productId));
          break;
        case 'changes':
          setChanges(await listSalesProductChanges(productId));
          break;
      }
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao carregar os registros.'));
    } finally {
      setLoading(false);
    }
  }, [activeTab, productId]);

  useEffect(() => {
    if (activeTab === 'backlog') return;
    collapseForm(resetForm);
    void loadItems();
    // Recarrega ao trocar a aba ou o produto. collapseForm muda a cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadItems, resetForm]);

  async function handleDelete(label: string, deleteFn: () => Promise<void>): Promise<void> {
    const confirmed = await confirm({
      title: 'Excluir',
      message: `Excluir ${label}?`,
      confirmLabel: 'Excluir',
      tone: 'danger',
    });
    if (!confirmed) return;
    try {
      await deleteFn();
      toast.success('Registro excluído.');
      resetForm();
      await loadItems();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao excluir.'));
    }
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setSaving(true);
    try {
      if (activeTab === 'requirements') {
        if (!requirementForm.title.trim()) {
          toast.error('Informe o que a solução precisa atender.');
          return;
        }
        if (!requirementForm.validationCriteria.trim()) {
          toast.error('Informe como o requisito será validado.');
          return;
        }
        if (editingId) {
          await updateSalesProductRequirement(productId, editingId, requirementForm);
        } else {
          await createSalesProductRequirement(productId, requirementForm);
        }
      } else if (activeTab === 'roadmap') {
        if (editingId) {
          await updateSalesProductRoadmapItem(productId, editingId, roadmapForm);
        } else {
          await createSalesProductRoadmapItem(productId, roadmapForm);
        }
      } else if (activeTab === 'risks') {
        if (editingId) {
          await updateSalesProductRisk(productId, editingId, riskForm);
        } else {
          await createSalesProductRisk(productId, riskForm);
        }
      } else if (activeTab === 'issues') {
        if (editingId) {
          await updateSalesProductIssue(productId, editingId, issueForm);
        } else {
          await createSalesProductIssue(productId, issueForm);
        }
      } else if (editingId) {
        await updateSalesProductChange(productId, editingId, changeForm);
      } else {
        await createSalesProductChange(productId, changeForm);
      }

      toast.success(editingId ? 'Registro atualizado.' : 'Registro criado.');
      if (editingId) {
        collapseForm(resetForm);
      } else {
        handleCreateSuccess(resetForm);
      }
      await loadItems();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao salvar.'));
    } finally {
      setSaving(false);
    }
  }

  async function handleChangeAction(itemId: string, action: 'submit' | 'approve' | 'reject'): Promise<void> {
    try {
      if (action === 'submit') {
        await submitSalesProductChange(productId, itemId);
      } else if (action === 'approve') {
        await approveSalesProductChange(productId, itemId);
      } else {
        const reason = window.prompt('Motivo da rejeição');
        if (!reason?.trim()) return;
        await rejectSalesProductChange(productId, itemId, reason.trim());
      }
      toast.success('Solicitação atualizada.');
      await loadItems();
    } catch (error) {
      toast.error(getRequestErrorMessage(error, 'Falha ao atualizar a solicitação.'));
    }
  }

  function renderForm() {
    if (activeTab === 'requirements') {
      return (
        <div className="grid gap-2 md:grid-cols-1">
          <label className="erp-label">
            O que precisa atender <RequiredMark />
            <input
              className="erp-input mt-1"
              required
              maxLength={200}
              value={requirementForm.title}
              onChange={(event) => setRequirementForm((current) => ({ ...current, title: event.target.value }))}
            />
          </label>
          <label className="erp-label">
            Como será validado <RequiredMark />
            <textarea
              className="erp-input mt-1"
              required
              rows={3}
              maxLength={4000}
              value={requirementForm.validationCriteria}
              onChange={(event) => setRequirementForm((current) => ({ ...current, validationCriteria: event.target.value }))}
            />
          </label>
        </div>
      );
    }

    if (activeTab === 'roadmap') {
      return (
        <div className="grid gap-2 md:grid-cols-4">
          <label className="erp-label md:col-span-2">
            Título <RequiredMark />
            <input
              className="erp-input mt-1"
              required
              value={roadmapForm.title}
              onChange={(event) => setRoadmapForm((current) => ({ ...current, title: event.target.value }))}
            />
          </label>
          <label className="erp-label">
            Tipo
            <select
              className="erp-input mt-1"
              value={roadmapForm.kind}
              onChange={(event) => setRoadmapForm((current) => ({ ...current, kind: event.target.value }))}
            >
              {ROADMAP_KIND_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <label className="erp-label">
            Status
            <select
              className="erp-input mt-1"
              value={roadmapForm.status}
              onChange={(event) => setRoadmapForm((current) => ({ ...current, status: event.target.value }))}
            >
              {ROADMAP_STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <label className="erp-label">
            Data planejada
            <input
              className="erp-input mt-1"
              type="date"
              value={toDateInput(roadmapForm.plannedDateUtc)}
              onChange={(event) =>
                setRoadmapForm((current) => ({
                  ...current,
                  plannedDateUtc: event.target.value ? `${event.target.value}T00:00:00.000Z` : null,
                }))
              }
            />
          </label>
          <label className="erp-label md:col-span-3">
            Descrição
            <input
              className="erp-input mt-1"
              value={roadmapForm.description ?? ''}
              onChange={(event) => setRoadmapForm((current) => ({ ...current, description: event.target.value }))}
            />
          </label>
        </div>
      );
    }

    if (activeTab === 'risks') {
      return (
        <div className="grid gap-2 md:grid-cols-4">
          <label className="erp-label md:col-span-2">
            Título <RequiredMark />
            <input
              className="erp-input mt-1"
              required
              value={riskForm.title}
              onChange={(event) => setRiskForm((current) => ({ ...current, title: event.target.value }))}
            />
          </label>
          <label className="erp-label">
            Probabilidade
            <select
              className="erp-input mt-1"
              value={riskForm.probability}
              onChange={(event) => setRiskForm((current) => ({ ...current, probability: event.target.value }))}
            >
              {RISK_PROBABILITY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <label className="erp-label">
            Impacto
            <select
              className="erp-input mt-1"
              value={riskForm.impact}
              onChange={(event) => setRiskForm((current) => ({ ...current, impact: event.target.value }))}
            >
              {RISK_IMPACT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <label className="erp-label">
            Status
            <select
              className="erp-input mt-1"
              value={riskForm.status}
              onChange={(event) => setRiskForm((current) => ({ ...current, status: event.target.value }))}
            >
              {RISK_STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <label className="erp-label md:col-span-3">
            Plano de mitigação
            <input
              className="erp-input mt-1"
              value={riskForm.mitigationPlan ?? ''}
              onChange={(event) => setRiskForm((current) => ({ ...current, mitigationPlan: event.target.value }))}
            />
          </label>
        </div>
      );
    }

    if (activeTab === 'issues') {
      return (
        <div className="grid gap-2 md:grid-cols-3">
          <label className="erp-label md:col-span-2">
            Título <RequiredMark />
            <input
              className="erp-input mt-1"
              required
              value={issueForm.title}
              onChange={(event) => setIssueForm((current) => ({ ...current, title: event.target.value }))}
            />
          </label>
          <label className="erp-label">
            Severidade
            <select
              className="erp-input mt-1"
              value={issueForm.severity}
              onChange={(event) => setIssueForm((current) => ({ ...current, severity: event.target.value }))}
            >
              {ISSUE_SEVERITY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <label className="erp-label">
            Status
            <select
              className="erp-input mt-1"
              value={issueForm.status}
              onChange={(event) => setIssueForm((current) => ({ ...current, status: event.target.value }))}
            >
              {ISSUE_STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <label className="erp-label md:col-span-2">
            Descrição
            <input
              className="erp-input mt-1"
              value={issueForm.description ?? ''}
              onChange={(event) => setIssueForm((current) => ({ ...current, description: event.target.value }))}
            />
          </label>
          <label className="erp-label md:col-span-3">
            Ação corretiva
            <input
              className="erp-input mt-1"
              value={issueForm.correctiveAction ?? ''}
              onChange={(event) => setIssueForm((current) => ({ ...current, correctiveAction: event.target.value }))}
            />
          </label>
        </div>
      );
    }

    const changeLocked = Boolean(editingId && changes.find((item) => item.id === editingId)?.status !== 'Draft');
    return (
      <div className="grid gap-2 md:grid-cols-2">
        <label className="erp-label">
          Título <RequiredMark />
          <input
            className="erp-input mt-1"
            required
            disabled={changeLocked}
            value={changeForm.title}
            onChange={(event) => setChangeForm((current) => ({ ...current, title: event.target.value }))}
          />
        </label>
        <label className="erp-label">
          Impacto no escopo
          <input
            className="erp-input mt-1"
            disabled={changeLocked}
            value={changeForm.impactScope ?? ''}
            onChange={(event) => setChangeForm((current) => ({ ...current, impactScope: event.target.value }))}
          />
        </label>
        <label className="erp-label">
          Impacto no prazo
          <input
            className="erp-input mt-1"
            disabled={changeLocked}
            value={changeForm.impactSchedule ?? ''}
            onChange={(event) => setChangeForm((current) => ({ ...current, impactSchedule: event.target.value }))}
          />
        </label>
        <label className="erp-label">
          Impacto no custo
          <input
            className="erp-input mt-1"
            disabled={changeLocked}
            value={changeForm.impactCost ?? ''}
            onChange={(event) => setChangeForm((current) => ({ ...current, impactCost: event.target.value }))}
          />
        </label>
        <label className="erp-label md:col-span-2">
          Descrição
          <textarea
            className="erp-input mt-1"
            rows={2}
            disabled={changeLocked}
            value={changeForm.description ?? ''}
            onChange={(event) => setChangeForm((current) => ({ ...current, description: event.target.value }))}
          />
        </label>
      </div>
    );
  }

  function renderList() {
    if (activeTab === 'requirements') {
      if (requirements.length === 0) {
        return <InlineAlert>Nenhum requisito cadastrado. Use o formulário acima para registrar o primeiro.</InlineAlert>;
      }
      return (
        <div className="erp-table-wrap">
          <div className="erp-table-scroll">
            <table className="erp-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Validação</th>
                  <th>Entregas</th>
                  {canWrite ? <th className="text-right">Ações</th> : null}
                </tr>
              </thead>
              <tbody>
                {requirements.map((item) => (
                  <tr key={item.id}>
                    <td className="max-w-md font-medium text-secondary-900" title={item.title}>
                      {truncateText(item.title, 200)}
                    </td>
                    <td className="max-w-md" title={item.validationCriteria}>
                      {truncateText(item.validationCriteria, 200)}
                    </td>
                    <td className="whitespace-nowrap">{item.backlogItemCount}</td>
                    {canWrite ? (
                      <td>
                        <div className="flex justify-end gap-1">
                          <EditIconButton
                            onClick={() => {
                              setEditingId(item.id);
                              expandForm();
                              setRequirementForm({
                                title: item.title,
                                validationCriteria: item.validationCriteria,
                              });
                            }}
                          />
                          <DeleteIconButton
                            onClick={() =>
                              void handleDelete(item.title, () => deleteSalesProductRequirement(productId, item.id))
                            }
                          />
                        </div>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    if (activeTab === 'roadmap') {
      if (roadmapItems.length === 0) {
        return <InlineAlert>Nenhum item de roadmap. Use o formulário acima para registrar o primeiro.</InlineAlert>;
      }
      return (
        <div className="erp-table-wrap">
          <div className="erp-table-scroll">
            <table className="erp-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Tipo</th>
                  <th>Status</th>
                  <th>Data</th>
                  {canWrite ? <th className="text-right">Ações</th> : null}
                </tr>
              </thead>
              <tbody>
                {roadmapItems.map((item) => (
                  <tr key={item.id}>
                    <td className="max-w-md font-medium text-secondary-900" title={item.title}>
                      {truncateText(item.title, 200)}
                    </td>
                    <td className="whitespace-nowrap">{labelFor(ROADMAP_KIND_OPTIONS, item.kind)}</td>
                    <td className="whitespace-nowrap">{labelFor(ROADMAP_STATUS_OPTIONS, item.status)}</td>
                    <td className="whitespace-nowrap">{formatDateBr(item.plannedDateUtc)}</td>
                    {canWrite ? (
                      <td>
                        <div className="flex justify-end gap-1">
                          <EditIconButton
                            onClick={() => {
                              setEditingId(item.id);
                              expandForm();
                              setRoadmapForm({
                                title: item.title,
                                description: item.description ?? '',
                                kind: item.kind,
                                status: item.status,
                                plannedDateUtc: item.plannedDateUtc,
                                actualDateUtc: item.actualDateUtc,
                                sortOrder: item.sortOrder,
                              });
                            }}
                          />
                          <DeleteIconButton
                            onClick={() => void handleDelete(item.title, () => deleteSalesProductRoadmapItem(productId, item.id))}
                          />
                        </div>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    if (activeTab === 'risks') {
      if (risks.length === 0) {
        return <InlineAlert>Nenhum risco. Use o formulário acima para registrar o primeiro.</InlineAlert>;
      }
      return (
        <ul className="divide-y divide-secondary-100">
          {risks.map((item) => (
            <li key={item.id} className="flex items-start justify-between gap-2 py-2">
              <div>
                <p className="font-medium text-secondary-900">{item.title}</p>
                <p className="text-xs text-secondary-500">
                  {labelFor(RISK_PROBABILITY_OPTIONS, item.probability)} · {labelFor(RISK_IMPACT_OPTIONS, item.impact)} · {labelFor(RISK_STATUS_OPTIONS, item.status)}
                </p>
              </div>
              {canWrite ? (
                <div className="flex shrink-0 gap-1">
                  <EditIconButton
                    onClick={() => {
                      setEditingId(item.id);
                      expandForm();
                      setRiskForm({
                        title: item.title,
                        description: item.description ?? '',
                        probability: item.probability,
                        impact: item.impact,
                        treatmentPriority: item.treatmentPriority,
                        mitigationPlan: item.mitigationPlan ?? '',
                        contingencyPlan: item.contingencyPlan ?? '',
                        status: item.status,
                        identifiedAtUtc: item.identifiedAtUtc,
                        closedAtUtc: item.closedAtUtc,
                      });
                    }}
                  />
                  <DeleteIconButton
                    onClick={() => void handleDelete(item.title, () => deleteSalesProductRisk(productId, item.id))}
                  />
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      );
    }

    if (activeTab === 'issues') {
      if (issues.length === 0) {
        return <InlineAlert>Nenhum problema. Use o formulário acima para registrar o primeiro.</InlineAlert>;
      }
      return (
        <ul className="divide-y divide-secondary-100">
          {issues.map((item) => (
            <li key={item.id} className="flex items-start justify-between gap-2 py-2">
              <div>
                <p className="font-medium text-secondary-900">{item.title}</p>
                <p className="text-xs text-secondary-500">
                  {labelFor(ISSUE_SEVERITY_OPTIONS, item.severity)} · {labelFor(ISSUE_STATUS_OPTIONS, item.status)}
                </p>
              </div>
              {canWrite ? (
                <div className="flex shrink-0 gap-1">
                  <EditIconButton
                    onClick={() => {
                      setEditingId(item.id);
                      expandForm();
                      setIssueForm({
                        title: item.title,
                        description: item.description ?? '',
                        severity: item.severity,
                        status: item.status,
                        correctiveAction: item.correctiveAction ?? '',
                        identifiedAtUtc: item.identifiedAtUtc,
                        resolvedAtUtc: item.resolvedAtUtc,
                      });
                    }}
                  />
                  <DeleteIconButton
                    onClick={() => void handleDelete(item.title, () => deleteSalesProductIssue(productId, item.id))}
                  />
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      );
    }

    if (changes.length === 0) {
      return <InlineAlert>Nenhuma mudança. Use o formulário acima para registrar a primeira.</InlineAlert>;
    }

    return (
      <ul className="divide-y divide-secondary-100">
        {changes.map((item) => (
          <li key={item.id} className="py-3">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="font-medium text-secondary-900">{item.title}</p>
                <p className="text-xs text-secondary-500">{CHANGE_STATUS_LABELS[item.status] ?? item.status}</p>
                {item.rejectionReason ? (
                  <p className="text-xs text-red-700">Motivo: {item.rejectionReason}</p>
                ) : null}
              </div>
              {canWrite ? (
                <div className="flex shrink-0 flex-wrap justify-end gap-1">
                  {item.status === 'Draft' ? (
                    <button
                      type="button"
                      className="rounded border border-secondary-300 px-2 py-0.5 text-xs text-secondary-800 hover:bg-secondary-50"
                      onClick={() => void handleChangeAction(item.id, 'submit')}
                    >
                      Enviar
                    </button>
                  ) : null}
                  {item.status === 'UnderReview' ? (
                    <>
                      <button
                        type="button"
                        className="rounded border border-secondary-300 px-2 py-0.5 text-xs text-secondary-800 hover:bg-secondary-50"
                        onClick={() => void handleChangeAction(item.id, 'approve')}
                      >
                        Aprovar
                      </button>
                      <button
                        type="button"
                        className="rounded border border-red-300 px-2 py-0.5 text-xs text-red-800 hover:bg-red-50"
                        onClick={() => void handleChangeAction(item.id, 'reject')}
                      >
                        Rejeitar
                      </button>
                    </>
                  ) : null}
                  {item.status === 'Draft' ? (
                    <>
                      <EditIconButton
                        onClick={() => {
                          setEditingId(item.id);
                          expandForm();
                          setChangeForm({
                            title: item.title,
                            description: item.description ?? '',
                            impactScope: item.impactScope ?? '',
                            impactSchedule: item.impactSchedule ?? '',
                            impactCost: item.impactCost ?? '',
                          });
                        }}
                      />
                      <DeleteIconButton
                        onClick={() => void handleDelete(item.title, () => deleteSalesProductChange(productId, item.id))}
                      />
                    </>
                  ) : null}
                </div>
              ) : null}
            </div>
            <ProductAzureLinksPanel
              applications={applications}
              links={item.azureLinks ?? []}
              canWrite={canWrite}
              onLink={async (payload) => {
                await linkSalesProductChangeAzure(productId, item.id, payload);
                await loadItems();
              }}
              onRefresh={async (linkId) => {
                await refreshSalesProductChangeAzureLink(productId, item.id, linkId);
                toast.success('Vínculo atualizado.');
                await loadItems();
              }}
              onDelete={async (linkId) => {
                await deleteSalesProductChangeAzureLink(productId, item.id, linkId);
                toast.success('Vínculo removido.');
                await loadItems();
              }}
            />
          </li>
        ))}
      </ul>
    );
  }

  if (activeTab === 'backlog') {
    return <ProductBacklogPanel productId={productId} canWrite={canWrite} />;
  }

  return (
    <div className="space-y-4">
      {canWrite ? (
        <CollapsibleFormSection
          title={editingId ? TAB_FORM_TITLES[activeTab].edit : TAB_FORM_TITLES[activeTab].create}
          expanded={formExpanded}
          onToggle={() => toggleOrCancel(resetForm)}
          editingSummary={editingId ? 'Editando registro — o formulário abriu em uma janela' : undefined}
          createSummary={TAB_FORM_TITLES[activeTab].summary}
          isEditing={Boolean(editingId)}
          className="mb-0"
          panelClassName="w-full max-w-5xl"
        >
          <form className="px-4 pb-4 pt-3" onSubmit={(event) => void handleSubmit(event)}>
            {renderForm()}
            <FormActionBar
              onCancel={() => collapseForm(resetForm)}
              cancelLabel="Cancelar"
              className="erp-btn-actions mt-4"
            >
              <button type="submit" className="erp-btn-primary" disabled={saving}>
                {saving ? 'Salvando...' : editingId ? 'Atualizar' : 'Criar'}
              </button>
            </FormActionBar>
          </form>
        </CollapsibleFormSection>
      ) : null}

      <PageCard>
        {loading ? <LoadingSpinner label="Carregando..." /> : renderList()}
      </PageCard>
    </div>
  );
}
